/**
 * Cover automation config helpers for `cover_external_control`.
 * Mirrors the backend model in `otto/src/services/automations/types.ts`.
 */

import type { Bilingual } from '../config/bilingual';
import { wireObject } from './wire';

// ---------------------------------------------------------------------------
// Types (mirror backend — no shared package yet)
// ---------------------------------------------------------------------------

export const COVER_EXTERNAL_CONTROL_TYPE = 'cover_external_control' as const;

export type CoverExternalSwitchMode = 'toggle' | 'open_stop_close' | 'up_down' | 'up_stop_down' | 'momentary';

export interface CoverExternalControlConfig {
  switch_mode: CoverExternalSwitchMode;
  open_position: number;
  close_position: number;
  momentary_direction: 'open' | 'close';
  debounce_ms: number;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export function getCoverExternalControlDefaults(): CoverExternalControlConfig {
  return {
    switch_mode: 'toggle',
    open_position: 0,
    close_position: 100,
    momentary_direction: 'open',
    debounce_ms: 50,
  };
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function num(value: unknown, fallback: number, min?: number, max?: number): number {
  const n =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : fallback;
  const resolved = Number.isFinite(n) ? n : fallback;
  return min !== undefined && max !== undefined ? clamp(resolved, min, max) : resolved;
}

export function normalizeCoverExternalControlConfig(raw?: unknown): CoverExternalControlConfig {
  const config = wireObject(raw) ?? {};
  const mode = config.switch_mode as CoverExternalSwitchMode;
  const switchMode: CoverExternalSwitchMode =
    mode === 'open_stop_close' || mode === 'up_down' || mode === 'up_stop_down' || mode === 'momentary'
      ? mode
      : 'toggle';
  return {
    switch_mode: switchMode,
    open_position: num(config.open_position, 0, 0, 100),
    close_position: num(config.close_position, 100, 0, 100),
    momentary_direction: config.momentary_direction === 'close' ? 'close' : 'open',
    debounce_ms: Math.max(0, num(config.debounce_ms, 50)),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function getCoverExternalControlErrors(config: unknown): string[] {
  const normalized = normalizeCoverExternalControlConfig(config);
  const errors: string[] = [];
  if (normalized.open_position > normalized.close_position) {
    errors.push('Open position must be lower than or equal to the close position.');
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Display model
// ---------------------------------------------------------------------------

export interface CoverExternalControlDisplay {
  automationId: string;
  name: string;
  icon: string;
  enabled: boolean;
  lastAction: string | null;
  lastPosition: number | null;
  config: CoverExternalControlConfig;
}

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function buildCoverExternalControlDisplay(
  automation: {
    id: string;
    name: string;
    icon?: string;
    enabled: boolean;
    config: unknown;
  },
  currentValues: Record<string, unknown>,
): CoverExternalControlDisplay {
  const lastAction = strOrNull(currentValues[`${COVER_EXTERNAL_CONTROL_TYPE}__last_action`]);
  return {
    automationId: automation.id,
    name: automation.name,
    icon: automation.icon || 'switch',
    enabled: automation.enabled,
    lastAction,
    lastPosition: numOrNull(currentValues[`${COVER_EXTERNAL_CONTROL_TYPE}__last_position`]),
    config: normalizeCoverExternalControlConfig(automation.config),
  };
}

export function isCoverExternalControlType(automationType: string): boolean {
  return automationType === COVER_EXTERNAL_CONTROL_TYPE;
}

/**
 * The mode/action words the sheet shows, both languages in the data and one
 * picked at render (`tt(label.en, label.sl)`) — the shape `sunTracker.ts`'s
 * `SUN_*_LABELS` established for the sun panel (card t_a8249a54 folded these
 * panels into the sheet language; the tables used to be English-only strings).
 */
export const COVER_SWITCH_MODE_LABELS: Record<CoverExternalSwitchMode, Bilingual> = {
  toggle: { en: 'Toggle', sl: 'Preklop' },
  open_stop_close: { en: 'Open / Stop / Close', sl: 'Odpri / Ustavi / Zapri' },
  up_down: { en: 'Up / Down', sl: 'Gor / Dol' },
  up_stop_down: { en: 'Open / Stop / Close buttons', sl: 'Tipke odpri / ustavi / zapri' },
  momentary: { en: 'Hold to move', sl: 'Drži za premik' },
};

export const COVER_ACTION_LABELS: Record<string, Bilingual> = {
  open: { en: 'Open', sl: 'Odpri' },
  close: { en: 'Close', sl: 'Zapri' },
  stop: { en: 'Stop', sl: 'Ustavi' },
  toggle_open: { en: 'Open', sl: 'Odpri' },
  toggle_close: { en: 'Close', sl: 'Zapri' },
};
